import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  Fab,
  CardMedia,
} from "@mui/material";
import SolarPowerIcon from "@mui/icons-material/SolarPower";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import StatsHero from "./StatsHero";
import SolarROILandingFancyParticles from "./ROICalculator";
import AOS from "aos";
import "aos/dist/aos.css";
import TestimonialsCarousel from "./Testimonials";
import { Link } from "react-router-dom";

const MainContent = () => {
  const [showTopBtn, setShowTopBtn] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });

    const handleScroll = () => {
      if (window.scrollY > 300) setShowTopBtn(true);
      else setShowTopBtn(false);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box sx={{ overflowX: "hidden", position: "relative" }}>
      {/* ===== HERO ===== */}
      <Box
        sx={{
          position: "relative",
          backgroundImage: "url('./images/solar-1.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          py: { xs: 10, md: 15 },
          textAlign: "center",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(255, 255, 255, 0.88) 0%, rgba(238, 240, 243, 0.7) 0%)",
            zIndex: 1,
          },
        }}
      >
        <Container
          sx={{ position: "relative", zIndex: 2 }}
          maxWidth="lg"
          data-aos="fade-down"
        >
          <Typography
            variant="h3"
            fontWeight="bold"
            mb={3}
            sx={{ color: "#2f4f7f" }}
          >
            Професионални препарати за почистване на соларни панели и фасади
          </Typography>

          <Typography variant="h6" mb={4} sx={{ color: "#4a5f7a" }}>
            Безопасно, бързо и ефективно
          </Typography>
          <Link
            to="./agents"
            onClick={() => {
              window.scroll(0, 0);
            }}
          >
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#2f4f7f",
                color: "#f2d35c",
                fontWeight: "bold",
                px: 4,
                py: 1.5,
                "&:hover": {
                  backgroundColor: "#1f3558",
                  transform: "scale(1.05)",
                },
                transition: "0.2s",
              }}
              endIcon={<SolarPowerIcon />}
            >
              Виж продуктите
            </Button>
          </Link>
        </Container>
      </Box>

      {/* ===== STATS ===== */}
      <Box data-aos="fade-up">
        <StatsHero />
      </Box>

      {/* ===== ROI ===== */}
      <Box data-aos="fade-up">
        <SolarROILandingFancyParticles />
      </Box>

      {/* ===== Testimonials ===== */}
      <Box data-aos="fade-up">
        <TestimonialsCarousel />
      </Box>

      {/* ===== Какво предлагаме ===== */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "#eaf0f8" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            fontWeight="bold"
            textAlign="center"
            mb={6}
            sx={{ color: "#2f4f7f" }}
            data-aos="fade-up"
          >
            Какво предлагаме
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                title: "Екологични препарати",
                text: "100% биоразградими и безопасни за околната среда.",
                icon: "🌱",
                bg: "linear-gradient(135deg, #e8f5e9, #ffffff)",
              },
              {
                title: "Професионално почистване",
                text: "Бързо и ефективно от опитен екип.",
                icon: "🧼",
                bg: "linear-gradient(135deg, #e3f2fd, #ffffff)",
              },
              {
                title: "Поддръжка и съвети",
                text: "Пълно обслужване и препоръки за максимална ефективност.",
                icon: "🛠️",
                bg: "linear-gradient(135deg, #f3e5f5, #ffffff)",
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index} data-aos="fade-up">
                <Card
                  sx={{
                    p: 3,
                    height: "100%",
                    background: item.bg,
                    borderRadius: "16px",
                    border: "1px solid #e0e6f1",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 14px 30px rgba(47,79,127,0.18)",
                    },
                  }}
                >
                  <CardContent>
                    {/* ICON */}
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        fontSize: 36,
                        borderRadius: "50%",
                        backgroundColor: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        boxShadow: "0 6px 14px rgba(0,0,0,0.1)",
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      mb={1}
                      sx={{ color: "#2f4f7f" }}
                    >
                      {item.title}
                    </Typography>

                    <Typography color="text.secondary">{item.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== Защо ние ===== */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "#f7f9fc" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            fontWeight="bold"
            textAlign="center"
            mb={6}
            sx={{ color: "#2f4f7f" }}
          >
            Защо да изберете нас
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                number: "#1",
                title: "Безопасни и ефективни продукти",
                text: "Нашите препарати са сертифицирани и надеждни.",
              },
              {
                number: "#2",
                title: "Над 7 години опит",
                text: "Доказан опит в индустрията за почистване на соларни панели.",
              },
              {
                number: "#3",
                title: "Доволни клиенти",
                text: "Реални резултати и положителни отзиви от нашите клиенти.",
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  sx={{
                    p: 4,
                    height: "100%",
                    borderRadius: 3,
                    backgroundColor: "#ffffff",
                    boxShadow: "0 12px 30px rgba(47,79,127,0.08)",
                    transition: "0.3s",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 18px 40px rgba(47,79,127,0.18)",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 42,
                      fontWeight: 700,
                      color: "#c4e69e",
                      opacity: 1,
                      mb: 1,
                    }}
                  >
                    {item.number}
                  </Typography>

                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    mb={1.5}
                    sx={{ color: "#2f4f7f" }}
                  >
                    {item.title}
                  </Typography>

                  <Typography color="text.secondary">{item.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== Нашите продукти ===== */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "#eaf0f8" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            fontWeight="bold"
            textAlign="center"
            mb={6}
            sx={{ color: "#2f4f7f" }}
          >
            Нашите продукти
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                title: "Solar Wash Protect 1 : 50 л",
                text: "Почиства ексременти от птици, прах, полени и сажди. 2 в 1 - почиства и придава антистатичен и водоотблъскващ филм.",
                image: "./images/agents/swp.avif",
              },
              { 
                title: "Antistatic Solar Agent 1:1000л",
                text: "Антистатичен филм и водоотблъскване с продължителност до 6 месеца. Допринася за повишаване на производителността до 15% повече.",
                image: "./images/agents/asa.avif",
              },
            ].map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ p: 3 }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      mb={2}
                      sx={{ color: "#2f4f7f" }}
                    >
                      {item.title}
                    </Typography>
                    <Typography mb={2}>{item.text}</Typography>
                    <CardMedia component="img"
                        image={item.image}
                        title={item.nameBulgarian}
                        sx={{
                          height: 180,
                          width: 140,
                          margin: "auto",
                          borderRadius: 1,
                        }}/>
                    <Button
                      variant="contained"
                      sx={{
                        mr: 2,
                        backgroundColor: "#2f4f7f",
                        color: "#f2d35c",
                        "&:hover": { backgroundColor: "#1f3558" },
                      }}
                    >
                      Научи повече
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== Реализирани проекти ===== */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            fontWeight="bold"
            textAlign="center"
            mb={5}
            sx={{ color: "#2f4f7f" }}
            data-aos="fade-up"
          >
            Реализирани проекти
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6} data-aos="fade-right">
              <Box
                component="img"
                src="/images/project-before.jpg"
                alt="Преди"
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  mb: 1,
                  objectFit: "cover",
                }}
              />
              <Typography>Преди</Typography>
            </Grid>
            <Grid item xs={12} md={6} data-aos="fade-left">
              <Box
                component="img"
                src="/images/project-after.jpg"
                alt="След"
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  mb: 1,
                  objectFit: "cover",
                }}
              />
              <Typography>След</Typography>
            </Grid>
          </Grid>
          <Typography mt={3} data-aos="fade-up">
            Почистихме 10 000 м² соларни панели. Ефективност +15%
          </Typography>
        </Container>
      </Box>

      {/* ===== Фасади ===== */}
      <Box sx={{ py: { xs: 6, md: 8 }, textAlign: "center" }}>
        <Container maxWidth="md">
          <Typography
            variant="h5"
            fontWeight="bold"
            mb={2}
            sx={{ color: "#2f4f7f" }}
          >
            Почистване на фасади с дронове
          </Typography>
          <Typography variant="body1" mb={3} color="text.secondary">
            Безопасно и ефективно почистване на фасади и соларни панели с
            дронове.
          </Typography>
          <Link
            to="./agents"
            onClick={() => {
              window.scroll(0, 0);
            }}
          >
            <Button
              variant="contained"
              href="/agents"
              sx={{
                backgroundColor: "#f2d35c",
                color: "#2f4f7f",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#e6c64f" },
              }}
            >
              Вижте повече за препаратите
            </Button>
          </Link>
        </Container>
      </Box>

      {/* ===== TOP BUTTON ===== */}
      {showTopBtn && (
        <Fab
          color="primary"
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            bottom: 40,
            right: 30,
            backgroundColor: "#2f4f7f",
            color: "#f2d35c",
            "&:hover": {
              backgroundColor: "#1f3558",
              transform: "scale(1.1)",
            },
          }}
          aria-label="Scroll to top"
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}
    </Box>
  );
};

export default MainContent;
